import type { ApiError, TimedAtom } from '@ctcells/api'
import type { Batch } from '@ctcells/contract'
import { enumLabel, t } from '@ctcells/i18n'

/**
 * **멈춘 작업을 어떤 단추로 잇는가**(백엔드 0.28.15 · `Batch.resume_route` · `resume_checks` · `resume_timed_atom_choices` ·
 * eric 2026-10-22 「일단 너가 말한 거 적용해줘」).
 *
 * | `resume_route` | [재개] | 잇는 길 |
 * | --- | --- | --- |
 * | `auto` | 서버 `allowed_commands` 그대로 | 코어가 스스로 잇는다(「자동으로 이어 갑니다」) |
 * | `resume` | 서버 `allowed_commands` 그대로 | 사람이 [재개] |
 * | `confirm` | **비활성 + 사유** | [복구] «멈춘 동작부터 다시»(`rerun_cut_atom` · `physical_checked` · `physical_checks`) |
 * | `null` | 종전 | 멈추지 않았다 |
 *
 * 🔴 **갈래는 서버 칸이다 — 문장(`halt_reason`)으로 가르지 않는다.** `confirm` 판정은 서버 재개 창구가 [재개]를
 *    `cut_confirm_required` 로 거절하는 판정과 같은 함수다(계약 원문). `allowed_commands` 는 상태표만 봐서 `resume` 을 그대로
 *    싣는다 — 그 거절을 미리 비활성 + 사유로 보이는 것은 비상정지 중 [재개]를 막는 것과 같은 결이다(`BatchStrip.reasonFor`).
 * 🔴 `halt_code`(멈춤 종류 코드)는 이 갈래의 입력이 아니다 — 서버가 «코드만으로 가르지 않는다»(낙하 · 파지 · 격리 · 재시도 남음)고
 *    적었고, 그 판정 결과가 `resume_route` 다. 화면은 코드를 «무슨 멈춤인가» 한 칸으로만 보인다(작업 상세).
 */
export type ResumeRoute = NonNullable<Batch['resume_route']>
export type ResumeCheck = NonNullable<Batch['resume_checks']>[number]

export function refusedResumeChecks(
  error: Pick<ApiError, 'context'> | null | undefined,
): ResumeCheck[] {
  if (error?.context?.['reason'] !== 'physical_check_required') return []
  const checks = error.context['checks']
  if (!Array.isArray(checks)) return []
  return checks.filter(
    (check): check is ResumeCheck =>
      check === 'door_area' ||
      check === 'robot_area' ||
      check === 'tray_positions' ||
      check === 'rotor_balanced',
  )
}

/** [재개]를 서버가 받지 않는 멈춤인가 — `resume_route=confirm` 하나로 가른다 */
export function needsConfirmResume(batch: Pick<Batch, 'resume_route'> | undefined): boolean {
  return batch?.resume_route === 'confirm'
}

/** 고를 수 있는 시간 원자 방식 — 🔴 **둘 이상일 때만** 고르기가 선다(계약 「화면은 둘일 때만 칸을 보인다」) */
export function timedAtomChoices(
  batch: Pick<Batch, 'resume_timed_atom_choices'> | undefined,
): readonly TimedAtom[] {
  const choices = batch?.resume_timed_atom_choices ?? []
  return choices.length >= 2 ? choices : []
}

/** 확인 칸 글자 — 사전에 없는 값은 원값이다(지어내지 않는다) */
export function resumeCheckLabel(check: string): string {
  return enumLabel('enum.resumeCheck', check)
}

const GENERAL_CHECK = 'general'

/** 체크 칸 목록 — 끊김이 요구하는 확인이 없으면 일반 한 칸(`CutAtomChecks` · `allChecked` 가 같은 목록을 읽는다) */
export function checkItems(checks: readonly ResumeCheck[]): { key: string; label: string }[] {
  return checks.length === 0
    ? [{ key: GENERAL_CHECK, label: t('resume.checkGeneral') }]
    : checks.map((check) => ({ key: check, label: resumeCheckLabel(check) }))
}

/** 체크 목록이 다 찼나 — 끊김이 요구하는 확인이 없으면 일반 한 칸이 찼는가 */
export function allChecked(checks: readonly ResumeCheck[], checked: ReadonlySet<string>): boolean {
  return checkItems(checks).every((item) => checked.has(item.key))
}
